/**
 * EnterpriseSchemas — Global JSON-LD structured data
 * Organization, LocalBusiness (3 offices), WebSite+SearchAction, Service, FAQPage
 * Injected once via MainLayout for all pages.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL } from '@/lib/seo';
import BRAND from '@/config/brand';
import { useLanguage } from '@/contexts/LanguageContextNew';

function injectSchemas(schemas: Record<string, unknown>[]) {
  // 1. Remove previous dynamic injections
  document.querySelectorAll('script[data-seo="enterprise-jsonld"]').forEach(el => el.remove());
  
  // 2. Remove static JSON-LD from index.html to prevent duplicates
  // Static scripts don't have data-seo attribute, so we target unattributed JSON-LD scripts
  document.querySelectorAll('script[type="application/ld+json"]:not([data-seo])').forEach(el => el.remove());
  
  for (const schema of schemas) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    let json: string;
    try {
      json = JSON.stringify(schema);
      // Escape </ sequences that break HTML parser inside <script> tags
      json = json.replace(/<\//g, '<\\/');
    } catch {
      continue; // Skip malformed schemas
    }
    script.textContent = json;
    script.setAttribute('data-seo', 'enterprise-jsonld');
    document.head.appendChild(script);
  }
}

const ORGANIZATION = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: BRAND.name,
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/logo.webp`,
    width: 512,
    height: 512,
  },
  description: BRAND.seo.description,
  foundingDate: '2018',
  email: BRAND.email.contact,
  telephone: BRAND.phone.main,
  sameAs: [
    BRAND.social.linkedin,
    BRAND.social.twitter,
    BRAND.social.facebook,
    BRAND.social.instagram,
    BRAND.social.github,
  ],
  address: [
    {
      '@type': 'PostalAddress',
      addressLocality: 'Casablanca',
      addressCountry: 'MA',
      streetAddress: 'Technopark, Route de Nouasseur',
      postalCode: '20000',
    },
    {
      '@type': 'PostalAddress',
      addressLocality: 'Madrid',
      addressCountry: 'ES',
      streetAddress: 'Business Center, Calle de la Princesa',
      postalCode: '28008',
    },
    {
      '@type': 'PostalAddress',
      addressLocality: 'Dubai',
      addressCountry: 'AE',
      streetAddress: 'Business Bay',
    },
  ],
  areaServed: [
    { '@type': 'Continent', name: 'Europe' },
    { '@type': 'Continent', name: 'North America' },
    { '@type': 'Continent', name: 'Africa' },
    { '@type': 'Continent', name: 'Asia' },
    { '@type': 'Place', name: 'Middle East' },
    { '@type': 'Continent', name: 'South America' },
    { '@type': 'Continent', name: 'Oceania' },
  ],
  knowsAbout: ['BPO', 'Call Center Outsourcing', 'Software Development', 'Digital Transformation', 'Customer Experience'],
  iso6523Code: '0060',
  hasCredential: [
    { '@type': 'EducationalOccupationalCredential', credentialCategory: 'ISO 27001' },
    { '@type': 'EducationalOccupationalCredential', credentialCategory: 'GDPR Compliant' },
    { '@type': 'EducationalOccupationalCredential', credentialCategory: 'PCI-DSS' },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '2847',
    bestRating: '5',
    worstRating: '1',
  },
  review: [
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Sarah Mitchell' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      reviewBody: 'Outstanding BPO partner with excellent multilingual capabilities.',
      datePublished: '2025-11-15',
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'James Chen' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      reviewBody: 'Professional service delivery and seamless integration with our systems.',
      datePublished: '2025-10-22',
    },
  ],
};

function makeLocalBusiness(city: string, country: string, countryCode: string, lat: number, lng: number, phone?: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#local-${city.toLowerCase()}`,
    name: `${BRAND.name} — ${city}`,
    url: SITE_URL,
    telephone: phone || BRAND.phone.main,
    email: BRAND.email.contact,
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressCountry: countryCode,
    },
    geo: { '@type': 'GeoCoordinates', latitude: lat, longitude: lng },
    parentOrganization: { '@id': `${SITE_URL}/#organization` },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
    priceRange: '€€',
  };
}

const LOCAL_BUSINESSES = [
  makeLocalBusiness('Casablanca', 'Morocco', 'MA', 33.5731, -7.5898, BRAND.phone.main),
  makeLocalBusiness('Madrid', 'Spain', 'ES', 40.4168, -3.7038),
  makeLocalBusiness('Dubai', 'UAE', 'AE', 25.2048, 55.2708, BRAND.phone.uae),
];

const WEBSITE = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: BRAND.name,
  url: SITE_URL,
  publisher: { '@id': `${SITE_URL}/#organization` },
  inLanguage: ['en', 'fr', 'es', 'ar', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'zh', 'ja', 'tr', 'ko', 'sv', 'da', 'hi', 'ro', 'cs', 'hu', 'fi', 'no', 'el', 'th', 'vi', 'id', 'ms', 'tl', 'bn'],
  potentialAction: {
    '@type': 'CommunicateAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/en/contact`,
    },
    name: 'Request a Quote',
  },
};

const SERVICES = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/#service-bpo`,
  name: 'BPO & Call Center Outsourcing',
  provider: { '@id': `${SITE_URL}/#organization` },
  serviceType: 'Business Process Outsourcing',
  areaServed: [
    { '@type': 'Continent', name: 'Europe' },
    { '@type': 'Continent', name: 'North America' },
    { '@type': 'Continent', name: 'Africa' },
    { '@type': 'Continent', name: 'Asia' },
    { '@type': 'Place', name: 'Middle East' },
    { '@type': 'Continent', name: 'South America' },
    { '@type': 'Continent', name: 'Oceania' },
  ],
  description: 'Premium multilingual BPO services including inbound customer care, technical support, outbound sales, and AI-powered operations from Casablanca, Madrid & Dubai.',
  offers: {
    '@type': 'Offer',
    priceCurrency: 'EUR',
    price: '3',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: '3',
      priceCurrency: 'EUR',
      unitText: 'hour',
    },
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'BPO Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Inbound Customer Care' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Technical Support' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Outbound Sales & Telemarketing' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Multilingual Support (32+ Languages)' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI-Ops & Back-Office' } },
    ],
  },
};

const DIGITAL_SERVICE = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/#service-digital`,
  name: 'Digital Factory & Software Development',
  provider: { '@id': `${SITE_URL}/#organization` },
  serviceType: 'Software Development',
  areaServed: [
    { '@type': 'Continent', name: 'Europe' },
    { '@type': 'Continent', name: 'North America' },
    { '@type': 'Continent', name: 'Africa' },
    { '@type': 'Continent', name: 'Asia' },
    { '@type': 'Place', name: 'Middle East' },
    { '@type': 'Continent', name: 'South America' },
    { '@type': 'Continent', name: 'Oceania' },
  ],
  description: 'Custom SaaS development, mobile apps, AI/ML solutions, UI/UX design, and cloud infrastructure from our Digital Factory.',
};

// FAQ_PAGE removed — each page injects its own FAQPage schema to avoid Google "Duplicate FAQPage field" errors

export function EnterpriseSchemas() {
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    injectSchemas([
      ORGANIZATION,
      ...LOCAL_BUSINESSES,
      WEBSITE,
      SERVICES,
      DIGITAL_SERVICE,
      // FAQ_PAGE removed to prevent duplicate FAQPage
    ]);
  }, [location.pathname, language]);

  return null;
}
